import { useUserStore } from '../../store/useUserStore';
import { useAnalysisStore } from '../../store/useAnalysisStore';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hook/useTranslation';
import { ANALYSIS_STATUS, VIDEO_RECORD_TYPE } from '../../data/define';
import Url from '../../data/url';
import IcArrowOn from '../../assets/image/ic_arrow_circle_right_top_green.svg';
import IcArrowOff from '../../assets/image/ic_arrow_circle_right_top_gray.svg';
import OneBtnAlert from '../common/OneBtnAlert';
import TwoBtnAlert from '../common/TwoBtnAlert';
import { Report } from '../../types';

interface AIAnalyticsProps {
  reports?: (Report & { isSuccess?: boolean })[];
}

export default function AIAnalytics({ reports = [] }: AIAnalyticsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useUserStore();
  const analysisState = useAnalysisStore((s) => s.analysisResult);
  const [analysisProgressAlertOpen, setAnalysisProgressAlertOpen] = useState(false);
  const [kidRegistrationAlertOpen, setKidRegistrationAlertOpen] = useState(false);

  const notCompletedReport = reports?.find((r) => !r.isSuccess);
  const isDraftMode = !user.isLoggedIn;

  const handleDailyVideoAnalysis = useCallback(() => {
    if (user?.kids && user.kids.length === 0) {
      setKidRegistrationAlertOpen(true);
      return;
    }
    if (isDraftMode) {
      navigate(Url.Login);
    } else {
      if (analysisState.status === ANALYSIS_STATUS.PENDING) {
        setAnalysisProgressAlertOpen(true);
      } else {
        navigate(`${Url.KidsRegisterUpload}?type=${encodeURIComponent(VIDEO_RECORD_TYPE.DAILY)}`, {
          state: notCompletedReport
            ? { kidId: user?.mainKid?.id, mi: [], reportId: notCompletedReport?.id }
            : { kidId: user?.mainKid?.id, mi: [] },
        });
      }
    }
  }, [navigate, user, analysisState, notCompletedReport, isDraftMode]);

  const handleQuestionVideoAnalysis = useCallback(() => {
    if (user?.kids && user.kids.length === 0) {
      setKidRegistrationAlertOpen(true);
      return;
    }
    if (isDraftMode) {
      navigate(Url.Login);
    } else {
      if (analysisState.status === ANALYSIS_STATUS.PENDING) {
        setAnalysisProgressAlertOpen(true);
      } else {
        navigate(`${Url.KidsRegisterUpload}?type=${encodeURIComponent(VIDEO_RECORD_TYPE.QNA)}`, {
          state: notCompletedReport
            ? {
                kidId: user?.mainKid?.id,
                mi: [],
                reportId: notCompletedReport?.id,
              }
            : {
                kidId: user?.mainKid?.id,
                mi: [],
              },
        });
      }
    }
  }, [navigate, user, analysisState, notCompletedReport, isDraftMode]);

  return (
    <div className="flex flex-col items-center justify-center mt-[24px] grid grid-cols-2 grid-rows-2 gap-[12px]">
      <div className="w-full h-[154px] bg-[#fff] rounded-[16px] flex flex-col items-start justify-between">
        <div className="text-[#1C1C1C] text-[16px] font-[700] text-left">
          {t('home.analytics.title').split('\n').map((line, i) => (
            <span key={i}>
              {line}
              {i === 0 && <br />}
            </span>
          ))}
        </div>
        <div className="relative inline-flex w-full px-[12px] text-[12px] text-[#CE4E4E] flex-col justify-center items-center h-[61px] bg-[#FFEDED] rounded-[16px] text-[#333] after:content-[''] after:absolute after:bottom-[-8px] after:left-[50%] after:translate-x-[-50%] after:border-transparent after:border-[8px] after:border-b-[0] after:border-t-[#FFEDED]">
          <div>
            <span className="font-[600]">{t('home.analytics.mission_soon_title')}</span>
            {t('home.analytics.mission_soon_desc')}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="w-full h-[154px] bg-[#fff] rounded-[16px] border border-[#EAEAEA] flex flex-col items-start justify-between p-[20px]"
        onClick={handleDailyVideoAnalysis}
      >
        <div className="flex flex-col items-start justify-start h-full">
          <div className="text-[#1C1C1C] text-[16px] font-[700]">{t('home.analytics.daily_title')}</div>
          <div className="text-left text-[14px] font-[400]">{t('home.analytics.daily_desc')}</div>
        </div>
        <img src={IcArrowOn} alt="arrow on" className="w-[24px] h-[24px] self-end" />
      </button>
      <div className="w-full h-[154px] bg-[#F3F3F3] rounded-[16px] border border-[#EAEAEA] flex flex-col items-start justify-between p-[20px]">
        <div className="flex flex-col items-start justify-start h-full">
          <div className="text-[#969696] text-[16px] font-[700]">{t('home.analytics.mission_title')}</div>
          <div className="text-left text-[14px] text-[#969696] font-[400]">
            {t('home.analytics.mission_desc')}
          </div>
        </div>
        <img src={IcArrowOff} alt="arrow off" className="w-[24px] h-[24px] self-end" />
      </div>
      <button
        type="button"
        onClick={handleQuestionVideoAnalysis}
        className="w-full h-[154px] bg-[#fff] rounded-[16px] border border-[#EAEAEA] flex flex-col items-start justify-between p-[20px]"
      >
        <div className="flex flex-col items-start justify-start h-full">
          <div className="text-[#1C1C1C] text-[16px] font-[700]">{t('home.analytics.qna_title')}</div>
          <div className="text-left text-[14px] font-[400]">{t('home.analytics.qna_desc')}</div>
        </div>
        <img src={IcArrowOn} alt="arrow on" className="w-[24px] h-[24px] self-end" />
      </button>
      <OneBtnAlert
        open={analysisProgressAlertOpen}
        onClose={() => setAnalysisProgressAlertOpen(false)}
        title={t('nav.alert.analysis_in_progress.title')}
        description={t('nav.alert.analysis_in_progress.description')}
        btnText={t('common.confirm')}
        onClick={() => setAnalysisProgressAlertOpen(false)}
      />
      <TwoBtnAlert
        open={kidRegistrationAlertOpen}
        onClose={() => setKidRegistrationAlertOpen(false)}
        title={t('home.alert.register_kid.title')}
        content={t('home.alert.register_kid.description')}
        leftBtnText={t('common.cancel')}
        rightBtnText={t('common.confirm')}
        leftBtnClick={() => setKidRegistrationAlertOpen(false)}
        rightBtnClick={() => navigate(Url.KidsRegister)}
      />
    </div>
  );
}
